import {
  Loader,
  ProviderConsumer,
  RadioGroup,
  Text,
} from "@fluentui/react-northstar";
import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { Languages } from "../../i18n/contants";
import FormLocationPropertiesDarkIcon from "../../images/preferences/right_pane_dark_mode.png";
import FormLocationPropertiesLightIcon from "../../images/preferences/right_pane_light_mode.png";
import FormLocationTabDarkIcon from "../../images/preferences/tab_view_dark_mode.png";
import FormLocationTabLightIcon from "../../images/preferences/tab_view_light_mode.png";
import { AppStateContext } from "../AppState";
import { Breadcrumbs } from "../shared/components/BreadcrumbNavigation";
import { Toolbar } from "../shared/components/Toolbar";
import { SETTINGS } from "../shared/utils/constants";
import { getThemeMode, toTitleCase } from "../shared/utils/helper";
import { ViewsSetting } from "./preferences/ViewsSetting";
import { CELL_EDITING_KEY, cellEditingOn } from "../shared/components/table/inlineCell";

export const Preferences: React.FC = () => {

  const storedTheme = getThemeMode();

  const { i18n, t } = useTranslation();
  const navs: Breadcrumbs = {
    breadcrumbs: [
      {
        title: toTitleCase(SETTINGS),
        link: "/settings",
      },
      {
        title: toTitleCase(t("preferences.preferences")),
        link: "",
      },
    ],
  };

  const items = [
    {
      name: "light",
      key: "Light",
      label: t("preferences.light"),
      value: "light",
    },
    { name: "dark", key: "Dark", label: t("preferences.dark"), value: "dark" },
    {
      name: "system",
      key: "System",
      label: t("preferences.system"),
      value: "system",
    },
  ];

  const requestItems = [
    {
      name: "abbreviated",
      key: "Abbreviated",
      label: t("preferences.abbreviated"),
      value: "abbreviated",
    },
    {
      name: "full",
      key: "Full",
      label: t("preferences.default"),
      value: "full",
    },
  ];

  const formLocationItems = [
    {
      name: "tab",
      key: "Tab",
      label: (
        <>
          <div className="cursor-pointer label">
            <Text content={t("preferences.form-location-tab")} />
          </div>
          <img className="mt-2" src={storedTheme == "dark" ? FormLocationTabDarkIcon : FormLocationTabLightIcon} />
        </>
      ),
      value: "tab",
    },
    {
      name: "properties",
      key: "Properties",
      label: (
        <>
          <div className="cursor-pointer label">
            <Text content={t("preferences.form-location-properties")} />
          </div>
          <img className="mt-2" src={storedTheme == "dark" ? FormLocationPropertiesDarkIcon : FormLocationPropertiesLightIcon} />
        </>

      ),
      value: "properties",
    },
  ];

  const [theme, setTheme] = useState(storedTheme || items[0].value);
  const [language, setLanguage] = useState(i18n.language);
  const [languageObj, setLanguageObj] = useState(
    Languages().filter((x) => x.isDefault)?.[0]
  );
  const [isSaving, setIsSaving] = useState(false);

  const requestLength = localStorage.getItem("TikitRequestLength");
  const [reqLength, setReqLength] = useState(
    requestLength || requestItems[0].value
  );
  const [formLocation, setFormLocation] = useState(localStorage.getItem("TikitCustomFormLocation") ?? formLocationItems[0].value);

  const handleChange = (e, p) => setTheme(p.value);
  const handleReqChange = (e, p) => setReqLength(p.value);
  const handleFormLocationChange = (e, p) => setFormLocation(p.value);
  const handleLanguageChange = (e, p) => {
    setLanguage(p.value);
    setLanguageObj(p);
  };

  const [tikitViewsHorizontalScroll, setTikitViewsHorizontalScroll] = useState(
    localStorage.getItem("TikitViewsHorizontalScroll") ?? "true"
  );

  const setViews = (tikitViewsHorizontalScroll: string) => {
    setTikitViewsHorizontalScroll(tikitViewsHorizontalScroll);
  };

  const [cellEditing, setCellEditing] = useState(String(cellEditingOn()));

  const saveData = () => {
    setIsSaving(true);
    localStorage.setItem("TikitTheme", theme);
    localStorage.setItem("TikitRequestLength", reqLength);
    localStorage.setItem("TikitCustomFormLocation", formLocation);
    localStorage.setItem(
      "TikitViewsHorizontalScroll",
      tikitViewsHorizontalScroll
    );
    localStorage.setItem(CELL_EDITING_KEY, cellEditing);
    let lsLangs = JSON.parse(
      localStorage.getItem("TikitLanguages")
    ) as Language[];
    lsLangs.forEach((x) => (x.isDefault = false));
    lsLangs.filter((x) => x.value === languageObj.value)[0].isDefault = true;
    localStorage.setItem("TikitLanguages", JSON.stringify(lsLangs));
    i18n.changeLanguage(languageObj.value);
    location.reload();
  };

  return (
    <AppStateContext.Consumer>
      {(state) => {
        return (
          <ProviderConsumer
            render={(globalTheme) => (
              <>
                <Helmet>
                  <title>{t("preferences.title-page")}</title>
                </Helmet>
                <Toolbar globalTheme={globalTheme} title={`Settings > Preferences`} breadcrumbs={navs} saveText={t("preferences.save")} saveCommand={saveData}
                  overrideLicenseCheck={true} />
                <div>
                  <div className="p-5 h-full overflow-auto">
                    {isSaving && (<div className={`text-center h-4/5 flex w-full justify-center`}><Loader label={t("preferences.loading-tikit-theme")} /></div>)}
                    {!isSaving && (
                      <>
                        <Text content={t("preferences.theme")} size="larger" />
                        <RadioGroup className="pt-3 flex space-x-10" defaultCheckedValue={theme} items={items} onCheckedValueChange={handleChange} />

                        <ViewsSetting horizontalScroll={tikitViewsHorizontalScroll == "true"} onChange={setViews} cellEditing={cellEditing === "true"} onCellEditingChange={setCellEditing} />

                        <div className="pt-5">
                          <Text content={t("preferences.request-length")} size="larger" />
                          <RadioGroup className="pt-3 flex space-x-10" defaultCheckedValue={reqLength} items={requestItems} onCheckedValueChange={handleReqChange} />
                        </div>

                        <div className="pt-5">
                          <Text content={t("preferences.form-location")} size="larger" />
                          <RadioGroup className="ticket-form-location pt-3 flex space-x-10" defaultCheckedValue={formLocation} items={formLocationItems} onCheckedValueChange={handleFormLocationChange} />

                        </div>

                        <div className="pt-5">
                          <Text content={t("preferences.language")} size="larger" />
                          <RadioGroup className="pt-3" vertical defaultCheckedValue={language} items={Languages()} onCheckedValueChange={handleLanguageChange} />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          />
        );
      }}
    </AppStateContext.Consumer>
  );
};
